import DOMPurify from 'dompurify';
import { GOOGLE_ADS_CONFIG } from './googleAdsConfig';
import { GOOGLE_ANALYTICS_CONFIG } from './googleAnalyticsConfig';

const MAX_INPUT_LENGTH = 2_000_000;

/** JSON data blocks must never acquire markup or executable statements. */
export function trustedJson(value: string): string | null {
  if (value.length > MAX_INPUT_LENGTH || !/^[\s]*[\[{]/.test(value)) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;
    return JSON.stringify(parsed).replace(/[<>&\u2028\u2029]/g, character =>
      `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
  } catch { return null; }
}

/** No arbitrary executable JavaScript is accepted, including by third parties. */
export function trustedScript(value: string): string {
  if (value === '') return '';
  const json = trustedJson(value);
  if (json === null) throw new TypeError('Executable script text is not allowed');
  return json;
}

/**
 * Stripe documents dynamic scripts on these dedicated script origins. Do not
 * broaden this to *.stripe.com, arbitrary HTTPS or same-origin user content.
 */
export function trustedScriptUrl(value: string): string {
  if (value.length > 2048 || /[\\\s]/.test(value)) throw new TypeError('Script URL is not allowed');
  let url: URL;
  try { url = new URL(value); } catch { throw new TypeError('Script URL is not allowed'); }
  if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash) {
    throw new TypeError('Script URL is not allowed');
  }
  if (/^(?:[a-z0-9-]+\.)?js\.stripe\.com$/.test(url.hostname)) return url.href;
  // A shared Google tag loads the other configured destination dynamically.
  // Permit only our two IDs and the observed runtime metadata, never arbitrary
  // containers, callbacks or a blanket Google-script origin exception.
  if (url.origin === 'https://www.googletagmanager.com'
      && ['/gtag/js', '/gtag/destination'].includes(url.pathname)) {
    const params = [...url.searchParams.entries()];
    const unique = new Set(params.map(([key]) => key)).size === params.length;
    const knownId = [GOOGLE_ADS_CONFIG.tagId, GOOGLE_ANALYTICS_CONFIG.tagId].some(id => url.searchParams.get('id') === id);
    const safeParams = params.every(([key, entry]) => key === 'id'
      || key === 'cx' && entry === 'c'
      || key === 'gtm' && /^[A-Za-z0-9_-]{1,256}$/.test(entry));
    if (unique && knownId && safeParams) return url.href;
  }
  throw new TypeError('Script URL is not allowed');
}

/**
 * Compatibility for React's current string sinks: actual HTML is sanitized;
 * data scripts are parsed and reserialized; the sole structural exception is
 * React's inert, empty script factory. There is no passthrough policy.
 */
export function trustedHtml(value: string): string {
  if (value.length > MAX_INPUT_LENGTH) throw new TypeError('HTML exceeds the content limit');
  if (value === '<script></script>') return value;
  const json = trustedJson(value);
  if (json !== null) return json;
  return DOMPurify.sanitize(value, { RETURN_TRUSTED_TYPE: false });
}

type PolicyFactory = {
  readonly defaultPolicy: unknown;
  createPolicy(name: string, rules: {
    createHTML(value: string): string;
    createScript(value: string): string;
    createScriptURL(value: string): string;
  }): unknown;
};

let installed = false;

/** Run before React, optional tags or any DOMParser-backed route is evaluated. */
export function initializeTrustedTypes(): void {
  if (installed || typeof window === 'undefined') return;
  const factory = (window as Window & { trustedTypes?: PolicyFactory }).trustedTypes;
  if (!factory) return;
  if (factory.defaultPolicy) throw new Error('An unexpected Trusted Types policy already exists');
  // DOMPurify owns its isolated parser policy. Initialize it before default so
  // parsing never recursively enters this compatibility sanitizer.
  DOMPurify.sanitize('', { RETURN_TRUSTED_TYPE: false });
  factory.createPolicy('default', {
    createHTML: trustedHtml,
    createScript: trustedScript,
    createScriptURL: trustedScriptUrl,
  });
  installed = true;
}
